import { FlexColumnSmallSpaced, FlexRowSpacedCentered } from '@fox/layout/src/flex';
import { PropsWithChildren, useContext, useState } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import styled, { css, useTheme } from 'styled-components';
import { formatFileSize } from '../../utils/number-functions';
import { appendAccessToken, FileAccessTokenContext } from '../file-access-token-context/index';
import { IconButton } from '../icon-button/index';
import { Icon, IconName, IconProps } from '../icon/index';
import { MuiAlert } from '../mui-alert/index';
import { Popover } from '../popover/index';
import { Typography } from '../typography/index';
import { uploadBlob } from './upload-blob';

export { uploadBlob } from './upload-blob';

export type GetSasFunc = (fileName: string) => Promise<string | undefined>;

export type BlobUploadedFunc = (blobUrl: string, mime: string, name: string, size: number) => void;

export type BlobFileDropProps = DropzoneOptions &
	PropsWithChildren<{
		getSas: GetSasFunc;
		onUploaded: BlobUploadedFunc;
		metadata?: Record<string, string>;
	}>;

export interface Attachment {
	blobUrl: string;
	mime: string;
	fileName: string;
	size: number;
}

export type FileDropperWithAttachmentsProps = {
	getSas: GetSasFunc;
	currentAttachments: Attachment[];
	onAttachCompleted: (attachments: Attachment[]) => Promise<void>;
	deleteAttachment: (index: number, url: string) => Promise<void>;
	allowedMimesMapping?: MimeMap;
	disableEditing?: boolean;
};

interface AttachmentCardProps extends Attachment {
	index: number;
	onRemoveAttachment: FileDropperWithAttachmentsProps['deleteAttachment'];
	allowedMimesMapping?: FileDropperWithAttachmentsProps['allowedMimesMapping'];
	disableEditing?: FileDropperWithAttachmentsProps['disableEditing'];
}

export type MimeMap = {
	[key: string]: [string[], IconName?];
};

export const MAX_FILE_UPLOAD_SIZE = 5242880;
// file upload size / number of bytes in 1MiB (using binary standard for readability: 1 MB = 1,048,576 bytes)
export const MAX_FILE_SIZE_MB = MAX_FILE_UPLOAD_SIZE / (1024 * 1024);

export const COMMON_IMAGE_ATTACHMENT_FORMATS_MIME_MAP: MimeMap = {
	'image/jpg': [['.jpg'], 'ImageMime'],
	'image/jpeg': [['.jpeg'], 'ImageMime'],
	'image/avif': [['.avif'], 'ImageMime'],
	'image/gif': [['.gif'], 'ImageMime'],
	'image/png': [['.png'], 'ImageMime'],
	'image/svg+xml': [['.svg'], 'ImageMime'],
	'image/tiff': [['.tif', '.tiff'], 'ImageMime'],
	'image/webp': [['.webp'], 'ImageMime']
};

export function getAllowedAttachmentMimes(allowedMimesMapping: MimeMap) {
	return Object.fromEntries(Object.entries(allowedMimesMapping).map(([key, value]) => [key, value[0]]));
}

// we want to be very strict about which mime types we allow in attachments
// The wildcard 'image/*' does not translate correctly to the map below, so image types have to be specified individually. Sadly repeating some code :(
export const COMMON_ATTACHMENT_FORMATS_MIME_MAP: MimeMap = {
	'application/msword': [['.doc', '.docx'], 'WordMime'],
	'application/pdf': [['.pdf'], 'PdfMime'],
	'application/vnd.ms-powerpoint': [['.ppt', '.pptx'], 'Report'],
	'application/rtf': [['.rtf']],
	'text/plain': [['.txt']],
	'text/csv': [['.csv', '.xls', '.xlsx'], 'ExcelMime'],
	'application/vnd.oasis.opendocument.text': [['.odt']],
	'application/vnd.sun.xml.writer': [['.sxw']],
	'message/rfc822': [['.eml'], 'SentEmail'],
	'application/vnd.ms-outlook': [['.msg'], 'SentEmail'],
	...COMMON_IMAGE_ATTACHMENT_FORMATS_MIME_MAP
};

const AttachmentIcon = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
`;

const AttachmentContainer = styled.div`
	display: grid;
	border: 1px solid ${props => props.theme.palette.swiftfox.neutral['40-justGrey']};
	min-width: 30%;
	border-radius: 5px;
	grid-template-columns: 20% 70% 10%;
`;
const AttachmentBoxMid = styled.div`
	display: flex;
	flex-direction: column;
	cursor: default;
	padding: 0.5rem;
	max-width: 90%;
`;

const AttachmentText = styled.div`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	max-height: 5em;
`;

const AttachmentBoxRight = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0.2rem;
	border-left: 1px solid ${props => props.theme.palette.swiftfox.neutral['40-justGrey']};
	&:hover {
		background-color: ${props => props.theme.palette.swiftfox.neutral['20-offWhite']};
	}
`;

const ThreeRowGrid = styled.div`
	display: grid;
	min-width: 35%;
	border-radius: 5px;
	grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
	gap: 5px;
`;

const AttachmentMenuItem = styled.div`
	display: flex;
	gap: 0.5rem;
	padding: 0.5rem;
	cursor: pointer;
	&:hover {
		background-color: ${props => props.theme.palette.swiftfox.neutral['20-offWhite']};
	}
`;

export const DropContainer = styled.div<{ $isDragAccept: boolean; $isDragReject: boolean; $isFocused: boolean; $disabled?: boolean }>`
	border-style: dashed;
	border-radius: 3px;
	border-width: 1px;
	text-align: center;
	width: 100%;
	cursor: pointer;
	padding: 1.25rem;
	${props => {
		if (props.$isDragAccept) {
			return css`
				border-width: 4px;
				color: ${props.theme.palette.success.contrastText};
				background: ${props.theme.palette.success.dark};
			`;
		}
		if (props.$isDragReject) {
			return css`
				border-width: 4px;
				color: ${props.theme.palette.error.contrastText};
				background: ${props.theme.palette.error.dark};
				cursor: no-drop;
			`;
		}
	}}
	border-color: ${props => {
		if (props.$isDragAccept) {
			return props.theme.palette.success.main;
		}
		if (props.$isDragReject) {
			return props.theme.palette.error.main;
		}
		if (props.$isFocused) {
			return props.theme.palette.primary.main;
		}
		return props.theme.palette.grey[500];
	}};
`;

export const blobDrop = (getSas: BlobFileDropProps['getSas'], onUploaded: BlobFileDropProps['onUploaded'], metadata?: Record<string, string>) =>
	async function onDrop(files: File[]) {
		for (const f of files) {
			const sas = await getSas(f.name);
			if (!sas) {
				// todo pretty alert
				alert('Unable to upload the file at this time');
				return;
			}
			const blobUrl = await uploadBlob(f, sas, metadata);
			onUploaded(blobUrl, f.type, f.name, f.size);
		}
	};

export type BlobUploadedWithDataUrlFunc = (blobUrl: string, dataUrl: string, mime: string, name: string, size: number) => void;

export const blobDropWithDataUrl = (getSas: BlobFileDropProps['getSas'], onUploaded: BlobUploadedWithDataUrlFunc) =>
	function onDrop(files: File[]) {
		for (const f of files) {
			// Create data URL first
			const reader = new FileReader();
			reader.onloadend = async () => {
				const dataUrl = reader.result as string;

				try {
					const sas = await getSas(f.name);
					if (!sas) {
						alert('Unable to upload the file at this time');
						return;
					}
					const blobUrl = await uploadBlob(f, sas);
					onUploaded(blobUrl, dataUrl, f.type, f.name, f.size);
				} catch (error) {
					console.error('Error uploading file:', error);
					alert('Failed to upload file');
				}
			};
			reader.readAsDataURL(f);
		}
	};

export function BlobFileDrop({ getSas, onUploaded, metadata, ...props }: BlobFileDropProps) {
	return <FileDropper {...props} onDrop={blobDrop(getSas, onUploaded, metadata)} />;
}
/** Component that handles the drop container and standard design of drop files */
export function FileDropper({ children, ...props }: PropsWithChildren<DropzoneOptions>) {
	const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone(props);
	const {
		isFocused: $isFocused,
		isDragAccept: $isDragAccept,
		isDragReject: $isDragReject,
		...rest
	} = getRootProps({
		isFocused,
		isDragAccept,
		isDragReject
	});
	return (
		<DropContainer $isFocused={$isFocused} $isDragAccept={$isDragAccept} $isDragReject={$isDragReject} {...rest}>
			<input {...getInputProps()} />
			{children}
		</DropContainer>
	);
}

export type FileInfo = { fileName: string; fileSize: number };
export type FileData<T extends File = File> = T;

type FileDropSingleProps = PropsWithChildren<
	{
		file?: FileInfo;
		clear: VoidFunction;
	} & DropzoneOptions
>;

export function FileDisplay({ file }: { file: FileInfo }) {
	return (
		<>
			<Icon iconName="AttachFile" />
			<Typography variant="body1" style={{ flex: 1 }}>
				{file?.fileName}
			</Typography>
			<Typography variant="body1">{formatFileSize(file?.fileSize)}</Typography>
		</>
	);
}

export function FileDropSingle({ file, clear, children, ...drop }: FileDropSingleProps) {
	const theme = useTheme();

	return file ? (
		<FlexRowSpacedCentered
			style={{ border: `1px solid ${theme.palette.swiftfox.neutral['50-midGrey']}`, borderRadius: '5px', padding: '0.5rem' }}
		>
			<FileDisplay file={file} />
			<IconButton iconName="Delete" iconColor="error" onClick={clear} />
		</FlexRowSpacedCentered>
	) : (
		<FileDropper maxFiles={1} multiple={false} {...drop}>
			{children}
		</FileDropper>
	);
}

export function FileDropperWithAttachments({
	onAttachCompleted,
	getSas,
	currentAttachments,
	deleteAttachment,
	allowedMimesMapping = COMMON_ATTACHMENT_FORMATS_MIME_MAP,
	disableEditing
}: FileDropperWithAttachmentsProps) {
	const [duplicate, setDuplicate] = useState<boolean>(false);

	const allowedAttachmentMimes = getAllowedAttachmentMimes(allowedMimesMapping);

	function addAttachment(item: Attachment) {
		let attachmentArr: Attachment[];
		if (currentAttachments?.find(a => a.fileName === item.fileName)) {
			setDuplicate(true);
		} else {
			setDuplicate(false);
			if (Array.isArray(currentAttachments)) {
				attachmentArr = [...currentAttachments, item];
			} else {
				attachmentArr = [item];
			}
			onAttachCompleted(attachmentArr);
		}
	}

	return (
		<FlexColumnSmallSpaced>
			{disableEditing ? (
				<></>
			) : (
				<>
					<BlobFileDrop
						multiple={true}
						getSas={getSas}
						accept={allowedAttachmentMimes}
						onUploaded={async (url, mime, fileName, size) => {
							addAttachment({ blobUrl: url, mime, fileName, size });
						}}
						maxSize={MAX_FILE_UPLOAD_SIZE}
					>
						<FlexRowSpacedCentered>
							<Icon iconName="AttachFile" />
							<Typography>Attachments: Click to add, or drop files</Typography>
						</FlexRowSpacedCentered>
					</BlobFileDrop>
					<Typography variant="caption">Max filesize 5MB.</Typography>{' '}
				</>
			)}

			{duplicate ? <MuiAlert severity="warning">The file has already been attached.</MuiAlert> : null}

			<AttachmentList
				disableEditing={disableEditing}
				currentAttachments={currentAttachments}
				allowedMimesMapping={allowedMimesMapping}
				deleteAttachment={deleteAttachment}
			/>
		</FlexColumnSmallSpaced>
	);
}

export const AttachmentList = ({
	currentAttachments,
	deleteAttachment,
	allowedMimesMapping,
	disableEditing
}: Pick<FileDropperWithAttachmentsProps, 'currentAttachments' | 'deleteAttachment' | 'allowedMimesMapping' | 'disableEditing'>) => {
	return (
		<ThreeRowGrid>
			{currentAttachments?.map((e, i) => {
				return (
					<AttachmentCard
						mime={e.mime}
						fileName={e.fileName}
						blobUrl={e.blobUrl}
						size={e.size}
						key={e.fileName}
						index={i}
						onRemoveAttachment={() => deleteAttachment(i, e.blobUrl)}
						allowedMimesMapping={allowedMimesMapping}
						disableEditing={disableEditing}
					/>
				);
			})}
		</ThreeRowGrid>
	);
};

const AttachmentCard = ({
	mime,
	fileName,
	blobUrl,
	size,
	index,
	onRemoveAttachment,
	allowedMimesMapping = COMMON_ATTACHMENT_FORMATS_MIME_MAP,
	disableEditing
}: AttachmentCardProps) => {
	return (
		<AttachmentContainer>
			<AttachmentIcon>
				<Icon iconName={allowedMimesMapping[mime]?.[1] ?? 'UnknownMime'} />
			</AttachmentIcon>
			<AttachmentBoxMid>
				<AttachmentText>{fileName}</AttachmentText>
				{(size ?? 0) > 0 ? <Typography variant="caption">{String(formatFileSize(size))}</Typography> : null}
			</AttachmentBoxMid>
			<AttachmentBoxRight>
				<Popover
					overlay={
						<AttachmentMenu
							index={index}
							url={blobUrl}
							removeAttachment={() => onRemoveAttachment(index, blobUrl)}
							disableEditing={disableEditing}
						/>
					}
				>
					<IconButton disableRipple={true} size="small" iconName="ArrowDropDown" />
				</Popover>
			</AttachmentBoxRight>
		</AttachmentContainer>
	);
};

const AttachmentMenu = ({
	index,
	url,
	removeAttachment,
	disableEditing
}: {
	index: number;
	url: string;
	removeAttachment: FileDropperWithAttachmentsProps['deleteAttachment'];
	disableEditing: FileDropperWithAttachmentsProps['disableEditing'];
}) => {
	const faContext = useContext(FileAccessTokenContext);

	const menuItems: {
		iconName: IconName;
		iconColor: IconProps['color'];
		title: string;
		action: () => void;
	}[] = [
		{
			iconName: 'FileDownload',
			iconColor: 'inherit',
			title: 'Download',
			action: () => window.open(appendAccessToken(url, faContext.items))
		}
	];
	if (!disableEditing) {
		menuItems.push({
			iconName: 'DeleteOutline',
			iconColor: 'error',
			title: 'Remove',
			action: () => removeAttachment(index, url)
		});
	}

	return (
		<FlexColumnSmallSpaced style={{ padding: '0.5rem' }}>
			{menuItems.map((e, i) => {
				return (
					<AttachmentMenuItem key={`${e.title}`} onClick={e.action}>
						<Icon iconName={e.iconName} color={e.iconColor} />
						<Typography>{e.title}</Typography>
					</AttachmentMenuItem>
				);
			})}
		</FlexColumnSmallSpaced>
	);
};
